import { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MoreVertical, Edit2, Trash2, GripVertical, Share2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ShareDialog } from './ShareDialog';
import { getIconByName } from '@/lib/icons';
import type { CollectionWithCount } from '@/types/database';
import type { DragData } from './DndProvider';

interface DroppableCollectionItemProps {
  collection: CollectionWithCount;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export function DroppableCollectionItem({
  collection,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
}: DroppableCollectionItemProps) {
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Sortable for reordering collections
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: collection.id,
    data: {
      type: 'collection',
      collection,
    } satisfies DragData,
  });

  // Droppable for receiving bookmark drops
  const { isOver, setNodeRef: setDroppableRef } = useDroppable({
    id: collection.id,
    data: {
      type: 'collection',
      collection,
    } satisfies DragData,
  });

  // Combine refs
  const setNodeRef = (node: HTMLElement | null) => {
    setSortableRef(node);
    setDroppableRef(node);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const IconComponent = getIconByName(collection.icon || 'folder');

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-1 px-2 py-2 rounded-md text-sm transition-all ${
        isSelected
          ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
          : 'text-sidebar-foreground hover:bg-sidebar-accent/50'
      } ${isOver ? 'ring-2 ring-primary bg-primary/10' : ''}`}
      {...attributes}
    >
      {/* Drag handle */}
      <div
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <GripVertical className="w-3 h-3 text-muted-foreground" />
      </div>

      {/* Collection button */}
      <button onClick={onSelect} className="flex-1 flex items-center gap-2 min-w-0">
        <div style={{ color: collection.color || '#0F766E' }}>
          <IconComponent className="w-4 h-4" />
        </div>
        <span className="flex-1 truncate text-left">{collection.name}</span>
        <span className="text-xs text-muted-foreground tabular-nums">
          {collection.bookmark_count}
        </span>
      </button>

      {/* Actions dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MoreVertical className="w-3 h-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setShareDialogOpen(true)}>
            <Share2 className="w-3 h-3 mr-2" />
            공유
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onEdit}>
            <Edit2 className="w-3 h-3 mr-2" />
            수정
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onDelete} className="text-destructive">
            <Trash2 className="w-3 h-3 mr-2" />
            삭제
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Share dialog */}
      <ShareDialog
        resourceType="collection"
        resourceId={collection.id}
        resourceName={collection.name}
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
      />
    </div>
  );
}
